import type {
  DetrixActionLine,
  DetrixBrowserResourceEntry,
  DetrixEvent,
  DetrixEventType,
  DetrixNetworkEntry,
  DetrixSession,
  DetrixStateSnapshot,
  DetrixUserStep,
} from './types';

const generateActions = (count: number): DetrixActionLine[] => {
  const actions: DetrixActionLine[] = [];
  const kinds: DetrixActionLine['kind'][] = ['click', 'keydown', 'input', 'scroll', 'navigate', 'error'];

  for (let i = 0; i < count; i++) {
    const kind = i === 45 ? 'error' : kinds[Math.floor(Math.random() * kinds.length)]!;
    actions.push({
      id: `action-${i}`,
      kind,
      summary:
        kind === 'error'
          ? 'Uncaught TypeError: Cannot read property "id" of undefined'
          : `${kind.charAt(0).toUpperCase() + kind.slice(1)} on element ${i % 5 === 0 ? 'button.submit' : 'input#search'}`,
      timestamp: i * 1500 + Math.floor(Math.random() * 500),
      eventId: `event-${i}`,
    });
  }
  return actions;
};

const generateResources = (count: number): DetrixBrowserResourceEntry[] => {
  const resources: DetrixBrowserResourceEntry[] = [];
  const types = ['script', 'stylesheet', 'image', 'fetch', 'xmlhttprequest', 'websocket', 'font'];

  for (let i = 0; i < count; i++) {
    const type = i === 10 ? 'websocket' : types[Math.floor(Math.random() * types.length)]!;
    resources.push({
      id: `res-${i}`,
      sequence: i,
      timestamp: i * 200,
      timing: { startMs: i * 200, endMs: i * 200 + 150, durationMs: 150 },
      chromeRequestId: `req-${i}`,
      resourceType: type,
      url: `https://static.detrix.dev/assets/${type}-${i}.js?v=1.2.3`,
      method: 'GET',
      status: i % 20 === 0 ? 404 : 200,
      statusLine: i % 20 === 0 ? 'HTTP/1.1 404 Not Found' : 'HTTP/1.1 200 OK',
      fromCache: i % 3 === 0,
    });
  }
  return resources;
};

const generateEvents = (count: number): DetrixEvent[] => {
  const events: DetrixEvent[] = [];
  for (let i = 0; i < count; i++) {
    const t: DetrixEventType = i % 10 === 0 ? 'navigation.push_state' : 'dom.click';
    events.push({
      id: `event-${i}`,
      eventVersion: 'v1',
      timestamp: i * 1000,
      sequence: i,
      type: t,
      payload:
        t === 'navigation.push_state'
          ? {
              fromUrl: 'https://app.detrix.dev/login',
              toUrl: 'https://app.detrix.dev/dashboard',
              kind: 'pushState' as const,
            }
          : { clientX: 120, clientY: 88, tagName: 'div' },
      selector: 'div#root > main',
    });
  }
  return events;
};

const network: DetrixNetworkEntry[] = [
  {
    id: 'net-1',
    sequence: 1,
    timestamp: 5000,
    timing: { startMs: 5000, endMs: 5450, durationMs: 450 },
    request: {
      requestId: 'req-1',
      url: 'https://api.detrix.dev/v1/sessions/search?q=debug',
      method: 'GET',
      headers: { Authorization: 'Bearer token_123', Accept: 'application/json' },
      body: null,
      bodyEncoding: 'empty',
    },
    response: {
      requestId: 'req-1',
      status: 200,
      statusText: 'OK',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ results: [], total: 0 }),
      bodyEncoding: 'json',
    },
  },
  {
    id: 'net-2',
    sequence: 2,
    timestamp: 12_000,
    timing: { startMs: 12_000, endMs: 15_000, durationMs: 3000 },
    request: {
      requestId: 'req-2',
      url: 'https://api.detrix.dev/v1/ingest',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: 'payload' }),
      bodyEncoding: 'json',
    },
    response: {
      requestId: 'req-2',
      status: 500,
      statusText: 'Internal Server Error',
      headers: { 'Content-Type': 'application/json' },
      body: '{"error": "Database timeout"}',
      bodyEncoding: 'text',
    },
  },
];

const steps: DetrixUserStep[] = [
  {
    id: 'step-1',
    kind: 'navigate',
    title: 'Navigate to Dashboard',
    subtitle: 'User entered the application',
    path: '/dashboard',
    timestampStart: 0,
    timestampEnd: 2000,
    sourceEventIds: ['event-0', 'event-1'],
  },
  {
    id: 'step-2',
    kind: 'type',
    title: 'Search for "error"',
    subtitle: 'Input burst in search bar',
    target: 'input#search',
    timestampStart: 5000,
    timestampEnd: 8000,
    sourceEventIds: ['event-10', 'event-11', 'event-12'],
  },
  {
    id: 'step-3',
    kind: 'error',
    title: 'Application Crash',
    subtitle: 'Uncaught TypeError detected',
    timestampStart: 45 * 1500,
    timestampEnd: 45 * 1500 + 500,
    sourceEventIds: ['event-45'],
  },
];

const stateSnapshots: DetrixStateSnapshot[] = [
  {
    id: 'snap-1',
    timestamp: 1000,
    sequence: 0,
    trigger: 'flush',
    url: 'https://app.detrix.dev/dashboard',
    storage: { localStorage: {}, sessionStorage: {} },
    viewport: { width: 1440, height: 900 },
  },
];

export const dummySession: DetrixSession = {
  sessionId: 'sess_9f2a1b3c4d5e6f7g8h9i0j',
  createdAt: '2026-04-15T12:00:00.000Z',
  metadata: {
    startUrl: 'https://app.detrix.dev/login',
    endUrl: 'https://app.detrix.dev/dashboard/settings',
    environment: {
      userAgent:
        'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
      platform: 'macOS',
      language: 'en-US',
      viewport: { width: 1440, height: 900 },
    },
  },
  events: generateEvents(100),
  network,
  stateSnapshots,
  browserResources: generateResources(250),
  steps,
  actions: generateActions(80),
};
